import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachTrials() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/academy/coach/trials`, { withCredentials: true });
      setRows(r.data.rows || []);
    } catch (_) { setRows([]); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, stage) => {
    try {
      await axios.post(`${API}/academy/trials/${id}/outcome`, { stage }, { withCredentials: true });
      toast.success(`Marked ${stage.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addNote = async (id) => {
    const text = window.prompt("Add a note about this trialist:");
    if (!text) return;
    try { await axios.post(`${API}/academy/trials/${id}/note`, { text }, { withCredentials: true }); toast.success("Note added"); }
    catch (e) { toast.error("Failed"); }
  };

  if (!loaded || rows.length === 0) return null;

  return (
    <Card className="p-4 mb-4 bg-white/5 border-white/10" data-testid="coach-trials-section">
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock className="w-5 h-5 text-cyan-400" />
        <h3 className="font-semibold text-white">Trialists assigned to you</h3>
      </div>
      <div className="space-y-2">
        {rows.map((t) => (
          <div key={t.id} className="bg-white/5 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`coach-trial-${t.id}`}>
            <div className="min-w-0">
              <span className="font-medium text-white">{t.child_name}</span>
              {t.age_group_label && <Badge className="ml-2 bg-slate-700 text-slate-200">{t.age_group_label}</Badge>}
              <p className="text-xs text-slate-300">{t.trial_date} {t.trial_time || ""} · {t.venue?.name || t.venue_name || "venue TBC"}</p>
              {t.venue?.address && <p className="text-xs text-slate-500">{t.venue.address}</p>}
              <p className="text-xs text-slate-400">Parent: {t.parent_name} ({t.parent_phone})</p>
              <p className="text-xs text-slate-500">
                {t.experience_level ? `Experience: ${t.experience_level}` : ""}
                {t.signup_notes ? ` · ${t.signup_notes}` : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500" onClick={() => act(t.id, "attended")} data-testid={`coach-trial-attended-${t.id}`}>Attended</Button>
              <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-500" onClick={() => act(t.id, "no_show")} data-testid={`coach-trial-noshow-${t.id}`}>No-show</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-slate-500" onClick={() => addNote(t.id)} data-testid={`coach-trial-note-${t.id}`}>Note</Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
